import { AxiosResponse } from 'axios';
import { omit } from 'lodash';
import { ApiService } from 'common/services';
import { ExtendedContentEntry, ScopeId, TemplateRule } from 'common/types/common';
import {
  PocRetailers,
  ProductFlagValue,
  RetailerType,
  selectAllValue,
  SyndigoRetailers,
  WalmartRetailers,
} from 'common/constants/entities';
import { userService } from 'common/services/user.service';
import { stringifySort } from 'common/utils/sort';
import { resolveApiBase } from 'common/utils/resolve-api-base';
import {
  AttachImageFlagBody,
  EnsureProductBody,
  ExportProductsCsvBody,
  ExportProductTitlesBody,
  ExportSupplierOneFormatData,
  ExtractExternalDataBody,
  GetAIGenerationsProductsParams,
  GetAIGenerationsProductsResponse,
  GetAIGenerationsStatusParams,
  GetAIGenerationsStatusResponse,
  GetProductBySkuBody,
  PerformImageAnalysisBody,
  PostApproximateCostEstimateBody,
  PostCostEstimateBody,
  ResetValueBody,
  SaveImagesOrderParams,
  SaveScrapedAttributesData,
  SuggestCategoriesData,
  TargetProductsFilters,
  TargetProductsListResponse,
} from './products-api.types';

function getTags(search: Array<string>) {
  const excludedFlags = Object.values(ProductFlagValue);
  const filteredTags = search.filter(s => s.includes(':'));
  const hasExcludedFlags = filteredTags.some(tag => excludedFlags.includes(tag as ProductFlagValue));
  const t = filteredTags.join(',').trim();

  if (!t.length) return undefined;
  return hasExcludedFlags ? t : t.replace(':', '@');
}

function getTCINs(search: Array<string>) {
  const ids =
    !userService.ensureRetailers(PocRetailers) &&
    !userService.ensureRetailers(SyndigoRetailers) &&
    !userService.ensureRetailer(RetailerType.Target) &&
    !userService.ensureRetailer(RetailerType.Flywheel) &&
    !userService.ensureRetailer(RetailerType.Autozone) &&
    !userService.ensureRetailer(RetailerType.ChickSaddle)
      ? search.filter(s => !s.includes(':') && /\d/.test(s) && Number.isInteger(Number(s)))
      : search.filter(s => !s.includes(':') && /\d/.test(s));

  if (!ids.length) return undefined;
  return userService.ensureRetailer(RetailerType.Flywheel) ? ids?.join(',') : ids;
}

function findName(values: string[]): string {
  const excludedFlags = Object.values(ProductFlagValue);
  const filteredValues = values.filter(value => !excludedFlags.some(flag => value.includes(flag)));
  const regex = /^\D+$/;

  // eslint-disable-next-line no-restricted-syntax
  for (const str of filteredValues) {
    if (regex.test(str)) {
      return str;
    }
  }

  return undefined;
}

function parseProductsFilters(retailer: RetailerType, args?: TargetProductsFilters) {
  if (!args) return undefined;

  const pid = retailer === RetailerType.Flywheel ? 'wpIDs' : 'tcins';

  const pidsList = retailer === RetailerType.Flywheel ? args?.tcins?.join(',') : args?.tcins;

  return {
    limit: args.limit,
    offset: args.offset,
    ...(args.sort ? { order: stringifySort(args.sort) } : {}),
    ...(args.scope_ids?.length && !args.scope_ids?.includes(selectAllValue)
      ? { scope_ids: args.scope_ids.join(',') }
      : {}),
    ...(args.category_id ? { category_id: args.category_id } : {}),
    ...(args.assigned_users?.length ? { assigned_users: args.assigned_users.join(',') } : {}),
    ...(args.approved_by?.length ? { approved_by: args.approved_by.join(',') } : {}),
    ...(args.actor_ids?.length ? { actor_ids: args.actor_ids.join(',') } : {}),
    ...(args.statuses?.length ? { statuses: args.statuses.join(',') } : {}),
    ...(args.content !== undefined ? { content: true } : {}),
    ...(args.extended !== undefined ? { extended: true } : {}),
    ...(args.skip_level !== undefined ? { skip_level: args.skip_level } : {}),
    ...(args.calc_attributes !== undefined ? { calc_attributes: args.calc_attributes } : {}),
    ...(args.tcins !== undefined ? { [pid]: args.tcins.length ? pidsList : '' } : {}),
    ...(args.categoryIDs?.length ? { category_ids: args.categoryIDs } : {}),
    ...(args.sub_vendor_id !== undefined ? { sub_vendor_id: args.sub_vendor_id } : {}),
    ...(args.flagged !== undefined ? { flagged: args.flagged } : {}),
    ...(args.import_id !== undefined ? { import_id: args.import_id } : {}),
    ...(args.search?.length
      ? {
          tags: getTags(args.search),
          [pid]: getTCINs(args.search),
          ...(findName(args.search) ? { search: findName(args.search) } : {}),
        }
      : {}),
  };
}

class ProductsApiService extends ApiService {
  getProducts = (
    retailer: RetailerType,
    args?: TargetProductsFilters
  ): Promise<AxiosResponse<TargetProductsListResponse>> => {
    return this.get(`/vendors/${retailer.toLowerCase()}/products`, {
      params: parseProductsFilters(retailer, args),
    });
  };

  getProductById = (retailer: RetailerType, id: string): Promise<AxiosResponse<{ product: ExtendedContentEntry }>> => {
    return this.get(`/vendors/${retailer.toLowerCase()}/products/${id}/content`);
  };

  getRetailerAttributes = (
    retailer: RetailerType,
    id: number | string
  ): Promise<AxiosResponse<{ attributes: Record<string, TemplateRule> }>> => {
    return this.get(`/vendors/${retailer.toLowerCase()}/categories/${id}/attributes`);
  };

  resetValue = (filters: ResetValueBody, retailer: string) => {
    return this.post(`/vendors/${retailer}/products/reset`, filters);
  };

  exportTargetCsv = (filters: ExportProductsCsvBody, retailer: string) => {
    return this.post(`/vendors/${retailer}/products/export`, filters);
  };

  exportAmazonProducts = (filters: ExportProductsCsvBody) => {
    return this.post('/vendors/poc/products/amazon-export', filters);
  };

  performWalmartOCR = (pids: Array<string>): Promise<AxiosResponse<{ commandID: string }>> => {
    return this.post('/content/ocr', { pids });
  };

  performTargetOCR = (retailer: RetailerType, pids: Array<string>): Promise<AxiosResponse<{ commandID: string }>> => {
    return this.post(`/vendors/${retailer.toLowerCase()}/products/ocr`, {
      ...(WalmartRetailers.includes(retailer) ? { pids } : { tcins: pids }),
    });
  };

  performWalmartBrandAnalysis = (pid: string): Promise<AxiosResponse<{ commandID: string }>> => {
    return this.post(`/content/${pid}/brand-analysis`);
  };

  performRetailerBrandAnalysis = (retailer: string, pid: string): Promise<AxiosResponse<{ commandID: string }>> => {
    return this.post(`/vendors/${retailer.toLowerCase()}/products/${pid}/brand-analysis`);
  };

  performWalmartImageAnalysis = (
    pid: string,
    body: PerformImageAnalysisBody
  ): Promise<AxiosResponse<{ commandID: string }>> => {
    return this.post(`/content/${pid}/image-analysis`, body);
  };

  performRetailerImageAnalysis = (
    retailer: string,
    pid: string,
    body: PerformImageAnalysisBody
  ): Promise<AxiosResponse<{ commandID: string }>> => {
    return this.post(`/vendors/${retailer.toLowerCase()}/products/${pid}/image-analysis`, body);
  };

  suggestCategories = (data: SuggestCategoriesData): Promise<AxiosResponse<{ id: string }>> => {
    return this.post(`/vendors/${data.retailer.toLowerCase()}/products/categories-suggestions`, {
      imageUrls: data?.imageUrls,
    });
  };

  suggestCategoriesWithFiles = (data: SuggestCategoriesData): Promise<AxiosResponse<{ id: string }>> => {
    const formData = new FormData();
    data.photos.forEach(p => {
      formData.append('files', p);
    });
    return this.post(`/vendors/${data.retailer.toLowerCase()}/products/categories-suggestions`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  };

  ensureProduct = (retailer: string, body: EnsureProductBody): Promise<AxiosResponse<{ id: string }>> => {
    return this.post(`/vendors/${retailer}/products/ensure-product`, omit(body, 'retailer'));
  };

  downloadCSV = (retailer: string, tcin: string): Promise<AxiosResponse<string>> => {
    return this.post(`/vendors/${retailer.toLowerCase()}/products/${tcin}/download-csv`);
  };

  saveScrapedAttributesData = (retailer: RetailerType, data: SaveScrapedAttributesData) => {
    return this.post(`/vendors/${retailer.toLowerCase()}/products/${data.tcin}/external-content`, data);
  };

  updateProductCategory = (retailer: RetailerType, tcin: string, categoryId: string) => {
    return this.post(`/vendors/${retailer.toLowerCase()}/products/${tcin}/category`, { category_id: categoryId });
  };

  addImagesXlsx = (retailer: string, files: Array<File>) => {
    const formData = new FormData();
    files.forEach(p => {
      formData.append('file', p);
    });

    return this.post(`/vendors/${retailer.toLowerCase()}/products/images-xlsx`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  };

  deleteWalmartProductById = (scope: ScopeId, id: string) => {
    return this.delete(`/scopes/${scope}/products/${id}`);
  };

  deleteRetailerProductById = (retailer: RetailerType, scope: ScopeId, id: string) => {
    return this.delete(`/vendors/${retailer.toLowerCase()}/scopes/${scope}/products/${id}`);
  };

  saveImagesOrder = (params: SaveImagesOrderParams) => {
    return this.patch(`/vendors/${params.retailer.toLowerCase()}/products/${params.pid}/images-order`, params.body);
  };

  getWalmartAIGenerationsProducts = (
    params: GetAIGenerationsProductsParams
  ): Promise<AxiosResponse<GetAIGenerationsProductsResponse>> => {
    return this.get('/ai-generations/cost', { params });
  };

  getAIGenerationsProducts = (
    retailer: RetailerType,
    params: GetAIGenerationsProductsParams
  ): Promise<AxiosResponse<GetAIGenerationsProductsResponse>> => {
    return this.get(`/vendors/${retailer.toLowerCase()}/ai-generations/cost`, { params });
  };

  getWalmartAIGenerationsCSV = (params: GetAIGenerationsProductsParams): Promise<AxiosResponse<string>> => {
    return this.get('/ai-generations/cost/csv', { params });
  };

  getAIGenerationsCSV = (
    retailer: RetailerType,
    params: GetAIGenerationsProductsParams
  ): Promise<AxiosResponse<string>> => {
    return this.get(`/vendors/${retailer.toLowerCase()}/ai-generations/cost/csv`, { params });
  };

  postEstimateCost = (
    retailer: RetailerType,
    body: PostCostEstimateBody
  ): Promise<AxiosResponse<{ totalCost: number }>> => {
    return this.post(`/vendors/${retailer.toLowerCase()}/ai-generations/cost/estimate`, body);
  };

  postEstimateApproximateCost = (
    retailer: RetailerType,
    body: PostApproximateCostEstimateBody
  ): Promise<AxiosResponse<{ totalCost: number }>> => {
    return this.post(`/vendors/${retailer.toLowerCase()}/ai-generations/cost/estimate/approximate`, body);
  };

  attachImageFlag = (retailer: RetailerType, body: AttachImageFlagBody) => {
    return this.post(`/vendors/${retailer.toLowerCase()}/products/${body?.pid}/images/flags`, omit(body, 'pid'));
  };

  detachImageFlag = (retailer: RetailerType, body: AttachImageFlagBody) => {
    return this.post(`/vendors/${retailer.toLowerCase()}/products/${body?.pid}/images/flags/detach`, omit(body, 'pid'));
  };

  getWalmartAiGenerationsStatus = (
    params: GetAIGenerationsStatusParams
  ): Promise<AxiosResponse<GetAIGenerationsStatusResponse>> => {
    return this.get('/ai-generations/status/pdp', { params });
  };

  getAiGenerationsStatus = (
    retailer: RetailerType,
    params: GetAIGenerationsStatusParams
  ): Promise<AxiosResponse<GetAIGenerationsStatusResponse>> => {
    return this.get(`/vendors/${retailer.toLowerCase()}/ai-generations/status/pdp`, { params });
  };

  extractExternalData = (retailer: RetailerType, body: ExtractExternalDataBody) => {
    return this.post(`/vendors/${retailer.toLowerCase()}/products/external-data-extraction/batches`, body);
  };

  extractProductExternalData = (retailer: RetailerType, productID: string) => {
    return this.post(`/vendors/${retailer.toLowerCase()}/products/${productID}/external-data-extraction`);
  };

  exportSupplierOneFormat = (retailer: RetailerType, data: ExportSupplierOneFormatData) => {
    return this.postFormData(`/vendors/${retailer.toLowerCase()}/products/fill-in-export`, data);
  };

  exportProductTitles = (
    retailer: string,
    body: ExportProductTitlesBody
  ): Promise<AxiosResponse<{ exportId: string }>> => {
    return this.post(`/vendors/${retailer.toLowerCase()}/products/titles/export`, body);
  };

  getProductBySku = (retailer: string, body: GetProductBySkuBody): Promise<AxiosResponse<{ tcin: string }>> => {
    return this.get(`/vendors/${retailer.toLowerCase()}/products/by-sku/${body?.sku}`, { params: omit(body, 'sku') });
  };
}

export const productsApi = new ProductsApiService(resolveApiBase());
